import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAccessibility } from '@/hooks/useAccessibility';
import { getChartTheme, CONECTAR_PRIMARY } from '../../theme/chartTheme';
import { DashPanel } from '../DashPanel';
import { DataCardHeader } from '../CardExportButton';

interface HourBlockDatum {
  label: string;
  count: number;
  intensity: number;
}

function useChartColors() {
  const { isDarkMode } = useAccessibility();
  return getChartTheme(isDarkMode);
}

/** Escala de calor real (frio → quente): azul, amarelo, vermelho. */
const HEAT_STOPS: [number, number, number][] = [
  [37, 99, 235],
  [250, 204, 21],
  [220, 38, 38],
];

const HEAT_GRADIENT_CSS = `linear-gradient(to right, rgb(${HEAT_STOPS[0].join(' ')}), rgb(${HEAT_STOPS[1].join(' ')}), rgb(${HEAT_STOPS[2].join(' ')}))`;

function heatColor(intensity: number): string {
  const t = Math.max(0, Math.min(1, intensity));
  const segment = t < 0.5 ? 0 : 1;
  const localT = t < 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const [r1, g1, b1] = HEAT_STOPS[segment];
  const [r2, g2, b2] = HEAT_STOPS[segment + 1];
  const r = Math.round(r1 + (r2 - r1) * localT);
  const g = Math.round(g1 + (g2 - g1) * localT);
  const b = Math.round(b1 + (b2 - b1) * localT);
  return `rgb(${r} ${g} ${b})`;
}

interface ClockLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  index: number;
}

/**
 * Distribuição por horário em formato de relógio: cada fatia é um bloco fixo de
 * 2h (como os números de um relógio), colorida pela intensidade de uso — sem
 * ponteiros, só o anel de calor.
 */
export function HourClockChart({
  title,
  data,
  onExport,
}: {
  title: string;
  data: HourBlockDatum[];
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const chartData = data.map((d) => ({ ...d, slice: 1 }));

  const renderLabel = ({ cx, cy, midAngle, outerRadius, index }: ClockLabelProps) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 16;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx + 1 ? 'start' : x < cx - 1 ? 'end' : 'middle'}
        dominantBaseline="central"
        fill={colors.axis}
        style={{ fontSize: 10 }}
      >
        {chartData[index].label}
      </text>
    );
  };

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="slice"
              nameKey="label"
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius="42%"
              outerRadius="72%"
              paddingAngle={2}
              isAnimationActive={false}
              label={renderLabel}
              labelLine={false}
            >
              {chartData.map((entry) => (
                <Cell key={entry.label} fill={heatColor(entry.intensity)} stroke="none" />
              ))}
            </Pie>
            <Tooltip
              formatter={(_value: number, _name: string, ctx: { payload: HourBlockDatum }) => [
                `${ctx.payload.count.toLocaleString('pt-BR')} usos`,
                ctx.payload.label,
              ]}
              contentStyle={{
                background: colors.tooltipBg,
                border: `1px solid ${colors.tooltipBorder}`,
                borderRadius: 4,
                fontSize: 12,
                color: colors.tooltipText,
                boxShadow: 'none',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[10px] text-muted-foreground shrink-0">Menos usos</span>
        <div className="h-2 flex-1 rounded-full" style={{ background: HEAT_GRADIENT_CSS }} />
        <span className="text-[10px] text-muted-foreground shrink-0">Mais usos</span>
      </div>
    </DashPanel>
  );
}

// Azul primeiro (marca), depois cores bem distintas entre si — evita fatias
// vizinhas de tons parecidos (ex.: azul e azul-céu) ficarem indistinguíveis.
const DONUT_COLORS = [CONECTAR_PRIMARY, '#F59E0B', '#10B981', '#8B5CF6', '#0EA5E9'] as const;

export interface DonutSlice {
  label: string;
  value: number;
}

/**
 * Distribuição por categoria em rosca + legenda — mesmo padrão do card
 * "Distribuição por perfil" do design system BI.
 */
export function DonutChart({
  title,
  data,
  onExport,
}: {
  title: string;
  data: DonutSlice[];
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      {total === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">Sem dados suficientes ainda</p>
      ) : (
        <div className="flex items-center gap-6">
          <div className="h-[120px] w-[120px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius="62%"
                  outerRadius="100%"
                  paddingAngle={2}
                  isAnimationActive={false}
                  stroke="none"
                >
                  {data.map((entry, idx) => (
                    <Cell key={entry.label} fill={DONUT_COLORS[idx % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString('pt-BR')} (${Math.round((value / total) * 100)}%)`,
                    name,
                  ]}
                  contentStyle={{
                    background: colors.tooltipBg,
                    border: `1px solid ${colors.tooltipBorder}`,
                    borderRadius: 4,
                    fontSize: 12,
                    color: colors.tooltipText,
                    boxShadow: 'none',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-2 text-xs min-w-0 flex-1">
            {data.map((entry, idx) => (
              <li key={entry.label} className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ background: DONUT_COLORS[idx % DONUT_COLORS.length] }}
                />
                <span className="text-foreground truncate flex-1">{entry.label}</span>
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {total > 0 ? Math.round((entry.value / total) * 100) : 0}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashPanel>
  );
}

export function GroupedColumnChart({
  title,
  data,
  nameKey,
  series,
  onExport,
}: {
  title: string;
  data: Record<string, string | number>[];
  nameKey: string;
  series: { key: string; label: string; color: string }[];
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const w = Math.max(280, data.length * 90);

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      {/* width/height fixos eliminam o loop ResizeObserver causado por minWidth + overflow-x-auto */}
      <div className="w-full overflow-x-auto scroll-styled">
        <BarChart width={w} height={220} data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={nameKey} tick={{ fill: colors.axis, fontSize: 11 }} axisLine={{ stroke: colors.grid }} tickLine={false} />
          <YAxis tick={{ fill: colors.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
          <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ background: colors.tooltipBg, border: `1px solid ${colors.tooltipBorder}`, borderRadius: 4, fontSize: 12, color: colors.tooltipText, boxShadow: 'none' }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[2, 2, 0, 0]} maxBarSize={18} isAnimationActive={false} />
          ))}
        </BarChart>
      </div>
    </DashPanel>
  );
}
